import { useEffect, useRef } from "react";

const ParticleBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let animationId;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // Star colors matching the galaxy theme
    const starColors = [
      "255,255,255",   // white
      "200,220,255",   // cold white-blue
      "147,197,253",   // light blue
      "96,165,250",    // blue
      "186,230,253",   // icy blue
    ];

    // Stars — tiny, dense, mostly static with slow twinkle
    const stars = Array.from({ length: 180 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      radius: Math.random() * 1.2 + 0.2,
      color: starColors[Math.floor(Math.random() * starColors.length)],
      baseOpacity: Math.random() * 0.6 + 0.2,
      opacity: 0,
      twinkleSpeed: Math.random() * 0.02 + 0.005,
      twinkleOffset: Math.random() * Math.PI * 2,
      vx: (Math.random() - 0.5) * 0.05,
      vy: (Math.random() - 0.5) * 0.05,
    }));

    // Bright prominent stars — larger, glow, twinkle more noticeably
    const brightStars = Array.from({ length: 20 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      radius: Math.random() * 2 + 1.5,
      color: starColors[Math.floor(Math.random() * 3)],
      baseOpacity: Math.random() * 0.4 + 0.6,
      opacity: 0,
      twinkleSpeed: Math.random() * 0.03 + 0.01,
      twinkleOffset: Math.random() * Math.PI * 2,
      vx: (Math.random() - 0.5) * 0.03,
      vy: (Math.random() - 0.5) * 0.03,
    }));

    // Nebula clouds — large soft blobs that slowly drift
    const nebulae = Array.from({ length: 5 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      radius: Math.random() * 180 + 100,
      vx: (Math.random() - 0.5) * 0.08,
      vy: (Math.random() - 0.5) * 0.08,
      opacity: Math.random() * 0.06 + 0.04,
      color: Math.random() > 0.5 ? "29,78,216" : "37,99,235",
    }));

    let t = 0;

    const drawStar = (s, isGlowing) => {
      s.opacity = s.baseOpacity * (0.6 + 0.4 * Math.sin(t * s.twinkleSpeed + s.twinkleOffset));

      // Core dot
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.radius, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${s.color}, ${s.opacity})`;
      ctx.fill();

      if (isGlowing) {
        // Soft halo glow for bright stars
        const glow = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.radius * 6);
        glow.addColorStop(0, `rgba(${s.color}, ${s.opacity * 0.5})`);
        glow.addColorStop(1, "transparent");
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.radius * 6, 0, Math.PI * 2);
        ctx.fillStyle = glow;
        ctx.fill();

        // Cross/sparkle for the brightest stars
        if (s.radius > 2.5) {
          ctx.save();
          ctx.globalAlpha = s.opacity * 0.4;
          ctx.strokeStyle = `rgba(${s.color}, 1)`;
          ctx.lineWidth = 0.5;
          const len = s.radius * 8;
          ctx.beginPath();
          ctx.moveTo(s.x - len, s.y);
          ctx.lineTo(s.x + len, s.y);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(s.x, s.y - len);
          ctx.lineTo(s.x, s.y + len);
          ctx.stroke();
          ctx.restore();
        }
      }

      // Drift + wrap
      s.x += s.vx;
      s.y += s.vy;
      if (s.x < 0) s.x = canvas.width;
      if (s.x > canvas.width) s.x = 0;
      if (s.y < 0) s.y = canvas.height;
      if (s.y > canvas.height) s.y = 0;
    };

    const draw = () => {
      t++;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Nebula blobs
      nebulae.forEach((n) => {
        const grad = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.radius);
        grad.addColorStop(0, `rgba(${n.color}, ${n.opacity})`);
        grad.addColorStop(1, "transparent");
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.radius, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();

        n.x += n.vx;
        n.y += n.vy;
        if (n.x < -n.radius) n.x = canvas.width + n.radius;
        if (n.x > canvas.width + n.radius) n.x = -n.radius;
        if (n.y < -n.radius) n.y = canvas.height + n.radius;
        if (n.y > canvas.height + n.radius) n.y = -n.radius;
      });

      // Regular stars
      stars.forEach((s) => drawStar(s, false));

      // Bright glowing stars
      brightStars.forEach((s) => drawStar(s, true));

      animationId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
      }}
    />
  );
};

export default ParticleBackground;
